import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react'

import {useDeviceLayout} from '../../../context/deviceLayout'
import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import minus from '../../../utils/minus'
import {Button} from '../Button'
import {Box} from '../helpers/Box'
import {Icon} from '../Icon'
import {Text} from '../Text'

export enum TooltipPlacement {
  Auto = 'auto',
  AutoStart = 'auto-start',
  AutoEnd = 'auto-end',
  Top = 'top',
  TopStart = 'top-start',
  TopEnd = 'top-end',
  Bottom = 'bottom',
  BottomStart = 'bottom-start',
  BottomEnd = 'bottom-end',
  Right = 'right',
  RightStart = 'right-start',
  RightEnd = 'right-end',
  Left = 'left',
  LeftStart = 'left-start',
  LeftEnd = 'left-end'
}

const TOOLTIP_MAX_WIDTH = 448
const ARROW_OFFSET = 4

interface Props {
  /** Open or closed state of the Tooltip */
  isOpen?: boolean
  /** Tooltip title */
  title?: string
  /** Tooltip content */
  content: ReactNode
  /** Tooltip placement relative to trigger component */
  placement?: TooltipPlacement
  /** Trigger component */
  children?: ReactNode
  /** Handler that fires when the tooltip is shown */
  onRender?: () => void
  /** Handler that fires when the tooltip opens */
  onOpen?: () => void
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Whether it should render inline */
  isInline?: boolean
  /** Anchor element to show Tooltip near it */
  anchor?: Element | null
  /** Passing this callback will show the close button.
   Pressing the button will cause the callback execution */
  onClose?: () => void
  /** An icon placed before the Tooltip content. It can be any type of React Element */
  icon?: ReactElement
}

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  height: ${cssTheme.layout.spacing.s600};
  color: ${cssTheme.colors.tooltip.content};
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${cssTheme.layout.spacing.s200};
`

const ButtonWrapper = styled.div`
  display: flex;
`

const CloseButton = styled(Button)`
  margin-inline-end: ${minus(cssTheme.layout.spacing.s250)};
`

const Row = styled(Box)`
  padding-inline: ${cssTheme.layout.spacing.s600};
  &.--has-icon {
    padding-inline-start: ${cssTheme.layout.spacing.s400};
  }
  &.--has-close-button {
    padding-inline-end: ${cssTheme.layout.spacing.s400};
  }
`

const StyledTooltip = styled.div`
  border-radius: ${cssTheme.layout.radius.md};
  box-shadow: ${cssTheme.shadows.floating};
  background-color: ${cssTheme.colors.tooltip.background};
  max-width: ${TOOLTIP_MAX_WIDTH}px;
  z-index: 4;
  ${linariaMq.desktopXs} {
    margin-left: 0;
    margin-right: 0;
  }
`

const StyledTriggerWrapper = styled.div`
  display: flex;
  pointer-events: auto;
  &.--is-inline {
    display: inline-flex;
  }
`

const StyledArrow = styled.div`
  width: 8px;
  height: 8px;
  position: absolute;
  background-color: ${cssTheme.colors.tooltip.background};
  transform: rotate(45deg);
  border-radius: ${cssTheme.layout.radius.xs} 0;

  &[data-side='top'] {
    border-radius: ${cssTheme.layout.radius.xs} 0;
  }

  &[data-side='bottom'] {
    border-radius: ${cssTheme.layout.radius.xs} 0;
  }

  &[data-side='left'] {
    border-radius: 0 ${cssTheme.layout.radius.xs};
  }

  &[data-side='right'] {
    border-radius: 0 ${cssTheme.layout.radius.xs};
  }
`

// Convert TooltipPlacement to floating-ui Placement
const convertPlacement = (placement: TooltipPlacement): Placement => {
  const placementMap: Record<TooltipPlacement, Placement> = {
    [TooltipPlacement.Auto]: 'bottom',
    [TooltipPlacement.AutoStart]: 'bottom-start',
    [TooltipPlacement.AutoEnd]: 'bottom-end',
    [TooltipPlacement.Top]: 'top',
    [TooltipPlacement.TopStart]: 'top-start',
    [TooltipPlacement.TopEnd]: 'top-end',
    [TooltipPlacement.Bottom]: 'bottom',
    [TooltipPlacement.BottomStart]: 'bottom-start',
    [TooltipPlacement.BottomEnd]: 'bottom-end',
    [TooltipPlacement.Right]: 'right',
    [TooltipPlacement.RightStart]: 'right-start',
    [TooltipPlacement.RightEnd]: 'right-end',
    [TooltipPlacement.Left]: 'left',
    [TooltipPlacement.LeftStart]: 'left-start',
    [TooltipPlacement.LeftEnd]: 'left-end'
  }
  return placementMap[placement]
}

export const Tooltip = ({
  children,
  title,
  content,
  placement = TooltipPlacement.Bottom,
  className,
  onRender,
  onOpen,
  isInline = true,
  isOpen = false,
  anchor = undefined,
  onClose,
  icon
}: Props) => {
  const [visible, setVisible] = useState(isOpen)
  const {isMobile} = useDeviceLayout()
  const arrowRef = useRef<HTMLDivElement>(null)
  const prevVisibleRef = useRef(isOpen)

  // Memoize computed values to prevent unnecessary re-renders
  const hasCloseButton = useMemo(
    () => Boolean(onClose) || isMobile,
    [onClose, isMobile]
  )
  const floatingPlacement = useMemo(
    () => convertPlacement(placement),
    [placement]
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setVisible(open)
      if (open && onOpen) {
        onOpen()
      }
    },
    [onOpen]
  )

  const {
    refs,
    floatingStyles,
    context,
    middlewareData,
    placement: finalPlacement
  } = useFloating({
    open: visible,
    onOpenChange: anchor ? undefined : handleOpenChange,
    placement: floatingPlacement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip(),
      shift(),
      arrow({
        element: arrowRef,
        padding: 12
      })
    ]
  })

  const arrowStyles = useMemo(() => {
    const arrowSide = finalPlacement.split('-')[0]
    const {x: arrowX, y: arrowY} = middlewareData.arrow || {}

    const styles: React.CSSProperties = {}
    if (arrowX != null) {
      styles.left = arrowX
    }
    if (arrowY != null) {
      styles.top = arrowY
    }

    // Position arrow on the correct side
    switch (arrowSide) {
      case 'top':
        styles.bottom = -ARROW_OFFSET
        break
      case 'bottom':
        styles.top = -ARROW_OFFSET
        break
      case 'left':
        styles.right = -ARROW_OFFSET
        break
      case 'right':
        styles.left = -ARROW_OFFSET
        break
    }

    return styles
  }, [finalPlacement, middlewareData.arrow])

  const hover = useHover(context, {
    enabled: !isMobile && !anchor
  })
  const focus = useFocus(context, {
    enabled: !anchor
  })
  const click = useClick(context, {
    enabled: !anchor
  })
  const dismiss = useDismiss(context)
  const role = useRole(context, {role: 'tooltip'})

  const {getReferenceProps, getFloatingProps} = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role
  ])

  const handleCloseButtonClick = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault()
      onClose?.()
      setVisible(false)
    },
    [onClose]
  )

  // Sync visible state with isOpen prop (for controlled/external usage)
  useEffect(() => {
    const wasVisible = prevVisibleRef.current
    prevVisibleRef.current = isOpen
    setVisible(isOpen)
    // Call onOpen when tooltip opens via isOpen prop (e.g., with anchor)
    if (!wasVisible && isOpen && onOpen) {
      onOpen()
    }
  }, [isOpen, onOpen])

  useEffect(() => {
    if (anchor) {
      refs.setReference(anchor)
    }
  }, [anchor, refs])

  useEffect(() => {
    if (visible && onRender) {
      onRender()
    }
  }, [visible, onRender])

  const renderTooltipContent = useCallback(
    () => (
      <Row
        display="flex"
        alignItems="flex-start"
        gap="s400"
        paddingTop="s500"
        paddingBottom="s500"
        className={cx(
          icon && '--has-icon',
          hasCloseButton && '--has-close-button'
        )}
      >
        {icon && <IconWrapper>{icon}</IconWrapper>}
        <ContentWrapper>
          {title && (
            <Text variant="labelM" colorPath="tooltip.content">
              {title}
            </Text>
          )}
          <Text variant="bodyS" colorPath="tooltip.content">
            {content}
          </Text>
        </ContentWrapper>
        {hasCloseButton && (
          <ButtonWrapper>
            <CloseButton
              hasTouchArea
              variant="ghostInverse"
              size="sm"
              iconStart={
                <Icon name="Close" size="sm" colorPath="tooltip.content" />
              }
              onClick={handleCloseButtonClick}
              aria-label="Close tooltip"
            />
          </ButtonWrapper>
        )}
      </Row>
    ),
    [icon, title, content, hasCloseButton, handleCloseButtonClick]
  )

  const arrowSide = finalPlacement.split('-')[0]

  if (children) {
    return (
      <>
        <StyledTriggerWrapper
          ref={anchor ? undefined : refs.setReference}
          className={cx(isInline && '--is-inline')}
          {...(!anchor ? getReferenceProps() : {})}
        >
          {children}
        </StyledTriggerWrapper>
        {visible && (
          <StyledTooltip
            ref={refs.setFloating}
            style={floatingStyles}
            className={className}
            {...getFloatingProps()}
          >
            {renderTooltipContent()}

            <StyledArrow
              ref={arrowRef}
              data-side={arrowSide}
              style={arrowStyles}
            />
          </StyledTooltip>
        )}
      </>
    )
  }

  // If we don't pass the children, we can render the tooltip near the anchor element
  return visible ? (
    <StyledTooltip
      ref={refs.setFloating}
      style={floatingStyles}
      className={className}
      {...getFloatingProps()}
    >
      {renderTooltipContent()}

      <StyledArrow ref={arrowRef} data-side={arrowSide} style={arrowStyles} />
    </StyledTooltip>
  ) : null
}
